import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from '../../context/AuthContext';
import './index.css';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading: authLoading, signOut } = useAuth();
  const profile = user?.user_metadata || {};
  const displayName = profile.full_name || profile.name || user?.email || '';
  const firstName = displayName.split(/[\s@]/)[0];

  const handleLogout = async () => {
    setMobileMenuOpen(false);
    try {
      await signOut();
    } catch (err) {
      console.error('Logout failed:', err.message);
    }
  };

  // Update navbar background styling when scrolled
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 100);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Smooth scroll to an in-page section, or navigate to home first
  const scrollToSection = (sectionId) => {
    if (location.pathname !== '/') {
      navigate(`/#${sectionId}`);
      return;
    }
    setTimeout(() => {
      const element = document.getElementById(sectionId);
      if (element) {
        const offset = 100;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
      }
    }, 100);
  };

  const handleContactClick = (e) => {
    e.preventDefault();
    if (location.pathname !== '/') {
      navigate('/#contact-form');
      return;
    }
    const formElement = document.getElementById('contact-form');
    const sectionElement = document.getElementById('contact');
    const targetElement = formElement || sectionElement;
    if (targetElement) {
      const offset = 100;
      const elementPosition = targetElement.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: Math.max(0, offsetPosition), behavior: 'smooth' });
    } else {
      setTimeout(() => {
        const retryFormElement = document.getElementById('contact-form');
        const retrySectionElement = document.getElementById('contact');
        const retryTarget = retryFormElement || retrySectionElement;
        if (retryTarget) {
          const offset = 100;
          const elementPosition = retryTarget.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - offset;
          window.scrollTo({ top: Math.max(0, offsetPosition), behavior: 'smooth' });
        }
      }, 300);
    }
    setMobileMenuOpen(false);
  };

  // Helper to execute click action and close mobile drawer
  const handleLinkClick = (handler) => {
    return (e) => {
      e.preventDefault();
      handler(e);
      setMobileMenuOpen(false);
    };
  };

  // Close mobile navigation drawer when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (mobileMenuOpen && !e.target.closest('.nav-links') && !e.target.closest('.mobile-menu-toggle')) {
        setMobileMenuOpen(false);
      }
    };

    if (mobileMenuOpen) {
      document.addEventListener('click', handleClickOutside);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  return (
    <>
      {mobileMenuOpen && (
        <div
          className="mobile-menu-overlay"
          onClick={(e) => {
            e.stopPropagation();
            setMobileMenuOpen(false);
          }}
        />
      )}
      <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
        <div className="nav-container">
          <Link to="/" className="logo" onClick={() => setMobileMenuOpen(false)}>
            Codeazy
          </Link>

          <ul className={`nav-links ${mobileMenuOpen ? 'mobile-open' : ''}`}>
            <li>
              <a href="#home" onClick={handleLinkClick((e) => { e.preventDefault(); scrollToSection('home'); })}>
                Home
              </a>
            </li>
            <li>
              <a href="#courses" onClick={handleLinkClick((e) => { e.preventDefault(); scrollToSection('courses'); })}>
                Courses
              </a>
            </li>
            <li>
              <NavLink to="/learn" onClick={() => setMobileMenuOpen(false)}>
                Learn
              </NavLink>
            </li>
            <li>
              <NavLink to="/interview-prep" onClick={() => setMobileMenuOpen(false)}>
                Interview Prep
              </NavLink>
            </li>
            <li>
              <NavLink to="/live-classes" onClick={() => setMobileMenuOpen(false)}>
                Live Classes
              </NavLink>
            </li>
            <li>
              <NavLink to="/blog" onClick={() => setMobileMenuOpen(false)}>
                Blog
              </NavLink>
            </li>
            <li>
              <a href="#about" onClick={handleLinkClick((e) => { e.preventDefault(); scrollToSection('about'); })}>
                About
              </a>
            </li>
            <li>
              <a href="#contact" onClick={handleLinkClick(handleContactClick)}>
                Contact
              </a>
            </li>
          </ul>

          <div className="nav-actions">
            {user ? (
              <>
                <span className="nav-user" title={user.email}>
                  {profile.avatar_url ? (
                    <img src={profile.avatar_url} alt="" referrerPolicy="no-referrer" />
                  ) : (
                    <span className="nav-user-initial">{firstName.charAt(0).toUpperCase()}</span>
                  )}
                  <span className="nav-user-name">{firstName}</span>
                </span>
                <button type="button" className="logout-button" onClick={handleLogout}>
                  Logout
                </button>
              </>
            ) : (
              !authLoading && (
                <Link to="/login" className="logout-button login-link" onClick={() => setMobileMenuOpen(false)}>
                  Login
                </Link>
              )
            )}
            <button
              className="mobile-menu-toggle"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setMobileMenuOpen(!mobileMenuOpen);
              }}
              aria-label="Toggle menu"
              type="button"
            >
              {mobileMenuOpen ? <CloseIcon sx={{ fontSize: 28, color: '#fff' }} /> : <MenuIcon sx={{ fontSize: 28, color: '#fff' }} />}
            </button>
          </div>
        </div>
      </nav>
    </>
  );
};

export default Navbar;